import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, MousePointer2, Clock, ClipboardList, Edit2, Copy, Scissors, Clipboard, Plus, Library, FolderPlus, Home } from 'lucide-react';
import { useStorageStore, NAME_COLLISION, UNSORTED_POSES_ID } from '../../store/useStorageStore';
import { ExplorerView } from './ExplorerView';
import { FolderTree } from './FolderTree';
import { useDanceStore } from '../../store/useDanceStore';
import { DanceState, SavedPose, Pose } from '../../types';
import { ThumbnailView } from '../ThumbnailView';
import { PoseNoteField } from '../PoseNoteField';
import { PosePreviewBox } from '../PosePreviewBox';
import { useIsMobile } from '../../hooks/useIsMobile';
import { trackPoseEvent } from '../../lib/analytics';


export const PosesModule: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const allFolders = useStorageStore(state => state.folders);
  const folders = React.useMemo(() => allFolders.filter(f => f.category === 'poses'), [allFolders]);
  const allPoses = useStorageStore(state => state.poses);
  const loadPose = useDanceStore((state: DanceState) => state.loadPose);
  const movePose = useStorageStore(state => state.movePose);
  
  const createFolder = useStorageStore(state => state.createFolder);
  const renameFolder = useStorageStore(state => state.renameFolder);
  const deleteFolder = useStorageStore(state => state.deleteFolder);
  const deletePose = useStorageStore(state => state.deletePose);
  const openInputDialog = useDanceStore((state: DanceState) => state.openInputDialog);
  const confirmAction = useDanceStore((state: DanceState) => state.confirmAction);

  const selectedFolderId = useStorageStore(state => state.selectedPoseFolderId);
  const setSelectedFolderId = useStorageStore(state => state.setSelectedPoseFolderId);
  const clipboard = useStorageStore(state => state.clipboard);
  const setClipboard = useStorageStore(state => state.setClipboard);
  const renamePose = useStorageStore(state => state.renamePose);
  const setSavedPoseNotes = useStorageStore(state => state.setSavedPoseNotes);
  const duplicatePose = useStorageStore(state => state.duplicatePose);
  const multiMovePoses = useStorageStore(state => state.multiMovePoses);
  const multiDeletePoses = useStorageStore(state => state.multiDeletePoses);

  const librarySelection = useDanceStore(state => state.librarySelection);
  const setLibrarySelection = useDanceStore(state => state.setLibrarySelection);
  const toggleLibrarySelection = useDanceStore(state => state.toggleLibrarySelection);

  const handleCreateFolder = (parentId: string | null) => {
    openInputDialog({
      title: parentId ? t('library.new_subfolder') : t('library.new_root_folder'),
      defaultName: t('library.new_folder'),
      placeholder: t('library.enter_folder_name'),
      submitLabel: t('library.create'),
      onSave: (name) => {
        let id: string;
        try {
          id = createFolder(name, 'poses', parentId);
        } catch (err: any) {
          if (err.message === NAME_COLLISION) {
            throw new Error(t('errors.folder_name_collision', 'A folder with that name already exists here.'));
          }
          throw err;
        }
        if (id) setSelectedFolderId(id);
      }
    });
  };

  const [hoveredPoseId, setHoveredPoseId] = useState<string | null>(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0, height: 0 });
  const isEditingPoseNoteRef = React.useRef(false);
  const hoverHideTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMouseOverCardRef = React.useRef(false);
  const isMouseOverPreviewRef = React.useRef(false);

  // Keeps the preview open while the cursor moves from the card onto the
  // popup itself (e.g. to click Edit/Save on the Pose Note), instead of
  // closing immediately on every onMouseLeave the way a plain hover would.
  const scheduleHidePreview = () => {
    if (isEditingPoseNoteRef.current) return;
    if (hoverHideTimeoutRef.current) clearTimeout(hoverHideTimeoutRef.current);
    hoverHideTimeoutRef.current = setTimeout(() => {
      if (!isMouseOverCardRef.current && !isMouseOverPreviewRef.current) {
        setHoveredPoseId(null);
      }
    }, 200);
  };

  // Always read the hovered pose live from the store, rather than holding a
  // stale snapshot — otherwise saving a Pose Note edit wouldn't be reflected
  // back in the still-open preview popup.
  const hoveredPose = hoveredPoseId ? allPoses.find(p => p.id === hoveredPoseId) ?? null : null;

  React.useEffect(() => {
    return () => {
      if (hoverHideTimeoutRef.current) clearTimeout(hoverHideTimeoutRef.current);
    };
  }, []);

  const currentPoses = React.useMemo(() => {
    return allPoses.filter(p => p.folderId === selectedFolderId);
  }, [allPoses, selectedFolderId]);

  // Changing the active folder clears the current card selection. Skips the
  // initial mount so a pre-set selection isn't wiped out immediately on
  // render (mirrors SequencesModule.tsx's identical fix).
  const previousFolderIdRef = React.useRef(selectedFolderId);
  React.useEffect(() => {
    if (previousFolderIdRef.current !== selectedFolderId) {
      previousFolderIdRef.current = selectedFolderId;
      setLibrarySelection([]);
    }
  }, [selectedFolderId]);

  const currentFolder = folders.find(f => f.id === selectedFolderId);
  const folderTitle = currentFolder ? currentFolder.name : t('library.select_folder');

  const currentPoseIds = React.useMemo(() => currentPoses.map(p => p.id), [currentPoses]);

  const handleDeleteSelected = () => {
    const selection = useDanceStore.getState().librarySelection;
    if (selection.length === 0) return;
    confirmAction({
      title: t('library.delete_selected_poses'),
      message: t('library.delete_selected_message', { count: selection.length }),
      onConfirm: () => {
        multiDeletePoses(selection);
        setLibrarySelection([]);
      }
    });
  };

  const handleCopySelected = () => {
    const selection = useDanceStore.getState().librarySelection;
    if (selection.length === 0) return;
    setClipboard({ type: 'copy', itemType: 'pose', itemIds: [...selection] });
    useDanceStore.getState().setToastMessage(t('library.copied_to_clipboard', { count: selection.length }));
  };

  const handleCutSelected = () => {
    const selection = useDanceStore.getState().librarySelection;
    if (selection.length === 0) return;
    setClipboard({ type: 'cut', itemType: 'pose', itemIds: [...selection] });
    useDanceStore.getState().setToastMessage(t('library.cut_to_clipboard', { count: selection.length }));
  };

  const handlePaste = () => {
    if (!clipboard || clipboard.itemType !== 'pose') return;
    
    if (clipboard.type === 'cut') {
      multiMovePoses(clipboard.itemIds, selectedFolderId);
      setClipboard(null);
      useDanceStore.getState().setToastMessage(t('library.moved_items', { count: clipboard.itemIds.length }));
    } else {
      // For copy, we duplicate each into the current folder
      clipboard.itemIds.forEach(id => {
        duplicatePose(id, selectedFolderId);
      });
      useDanceStore.getState().setToastMessage(t('library.pasted_items', { count: clipboard.itemIds.length }));
    }
  };

  React.useEffect(() => {
    if (isMobile) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.key) return;
      // Only handle if this module is likely active (e.g. library selection exists or focused)
      if (useDanceStore.getState().activeModule !== 'Poses') return;
      if (document.activeElement?.tagName === 'INPUT') return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        handleDeleteSelected();
      } else if (e.key === 'Escape') {
        setLibrarySelection([]);
      } else if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
        handleCopySelected();
      } else if (e.key === 'x' && (e.ctrlKey || e.metaKey)) {
        handleCutSelected();
      } else if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
        handlePaste();
      } else if (e.key === 'd' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        librarySelection.forEach(id => duplicatePose(id));
      } else if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setLibrarySelection(currentPoseIds);
      } else if (e.key === 'F2' && librarySelection.length === 1) {
        const pose = allPoses.find(p => p.id === librarySelection[0]);
        if (pose) {
          openInputDialog({
            title: t('library.rename'),
            defaultName: pose.name,
            onSave: (newName) => renamePose(pose.id, newName)
          });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [librarySelection, selectedFolderId, clipboard, currentPoseIds, isMobile]);

  const sidebar = (
    <FolderTree 
      folders={folders}
      selectedFolderId={selectedFolderId}
      onSelectFolder={setSelectedFolderId}
      onCreateFolder={handleCreateFolder}
      onRenameFolder={renameFolder}
      onDeleteFolder={deleteFolder}
      onMoveItem={movePose}
      title={t('library.poses')}
    />
  );

  const content = (
    <div 
      style={{ height: '100%', overflowY: 'auto', position: 'relative' }}
      onClick={() => setLibrarySelection([])}
      onDragOver={(e) => {
        if (isMobile) return;
        const hasSource = e.dataTransfer.types.some(t => t.toLowerCase() === 'sourcetype');
        if (hasSource) {
          e.preventDefault();
          if (e.ctrlKey || e.metaKey) {
            e.dataTransfer.dropEffect = 'copy';
          } else {
            e.dataTransfer.dropEffect = 'none';
          }
        }
      }}
      onDrop={(e) => {
        if (isMobile) return;
        e.preventDefault();
        const sourceIdsStr = e.dataTransfer.getData('sourceId');
        const sourceType = e.dataTransfer.getData('sourceType');
        if (sourceIdsStr && sourceType === 'pose') {
          const ids = sourceIdsStr.split(',');
          const isCopy = e.ctrlKey || e.metaKey;
          if (isCopy) {
            ids.forEach(id => {
              duplicatePose(id, selectedFolderId);
            });
            useDanceStore.getState().setToastMessage(t('library.copied_to_clipboard', { count: ids.length }));
          }
        }
      }}
    >
      {currentPoses.length === 0 ? (
        <div style={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
          {t('library.no_poses')}
        </div>
      ) : (
        <div 
          style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, 76px)', 
            gap: '12px', 
            padding: '16px' 
          }}
        >
          {currentPoses.map((pose, index) => (
            <div 
              key={pose.id}
              id={index === 0 ? 'library-first-pose-card' : undefined}
              className={`explorer-card ${librarySelection.includes(pose.id) ? 'selected' : ''}`}
              draggable={!isMobile}
              onDragStart={(e) => {
                if (isMobile) return;
                setHoveredPoseId(null);
                // If dragging a selected item, move all selected items
                const dragIds = librarySelection.includes(pose.id) ? librarySelection : [pose.id];
                e.dataTransfer.setData('sourceId', dragIds.join(','));
                e.dataTransfer.setData('sourceType', 'pose');
                // Without this, the native copy cursor can never show during
                // a Ctrl-drag no matter what dropEffect a drop target sets
                // (the exact bug sequence cards had and fixed — see
                // SequencesModule.tsx's matching onDragStart).
                e.dataTransfer.effectAllowed = 'copyMove';
              }}
              style={{
                width: '70px',
                height: '76px',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1,
                overflow: 'visible',
                borderRadius: '8px',
                border: librarySelection.includes(pose.id) ? '2px solid #38bdf8' : '2px solid transparent',
                background: librarySelection.includes(pose.id) ? 'rgba(56, 189, 248, 0.1)' : 'transparent',
                transition: 'all 0.15s ease'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (isMobile) {
                  loadPose(pose.pose, pose.cameraState, { type: 'library', id: pose.id, name: pose.name });
                  useDanceStore.getState().setActiveModule('PoseDesigner');
                } else {
                  toggleLibrarySelection(pose.id, e.ctrlKey || e.metaKey, e.shiftKey, currentPoseIds);
                }
              }}
              onMouseEnter={(e) => {
                if (isMobile) return;
                isMouseOverCardRef.current = true;
                if (hoverHideTimeoutRef.current) {
                  clearTimeout(hoverHideTimeoutRef.current);
                  hoverHideTimeoutRef.current = null;
                }
                setHoveredPoseId(pose.id);
                const rect = e.currentTarget.getBoundingClientRect();
                setHoverPos({ x: rect.left + rect.width / 2, y: rect.top, height: rect.height });
              }}
              onMouseLeave={() => {
                isMouseOverCardRef.current = false;
                scheduleHidePreview();
              }}
              onDoubleClick={() => {
                if (isMobile) return;
                loadPose(pose.pose, pose.cameraState, { type: 'library', id: pose.id, name: pose.name });
                useDanceStore.getState().setActiveModule('PoseDesigner');
              }}
              onContextMenu={(e) => {
                if (isMobile) return;
                e.preventDefault();
                // Ensure the item is selected if it wasn't
                let targetSelection = librarySelection;
                if (!librarySelection.includes(pose.id)) {
                  targetSelection = [pose.id];
                  setLibrarySelection(targetSelection);
                }

                const openContextMenu = useDanceStore.getState().openContextMenu;
                const applyPoseToTime = useDanceStore.getState().applyPoseToTime;
                const currentTime = useDanceStore.getState().currentTime;
                
                openContextMenu(e.clientX, e.clientY, [
                  {
                    label: t('library.use_as_base'),
                    icon: MousePointer2,
                    disabled: targetSelection.length > 1,
                    action: () => {
                      loadPose(pose.pose, pose.cameraState, { type: 'library', id: pose.id, name: pose.name });
                      useDanceStore.getState().setActiveModule('PoseDesigner');
                    }
                  },
                  {
                    label: t('library.apply_to_frame'),
                    icon: Clock,
                    disabled: targetSelection.length > 1,
                    action: () => {
                      applyPoseToTime(pose.pose, currentTime, pose.cameraState);
                      trackPoseEvent('applied_to_frame', { pose_name: pose.name, keyframe: currentTime });
                      useDanceStore.getState().setToastMessage(`Applied "${pose.name}" to frame ${currentTime.toFixed(2)}s`);
                    }
                  },
                  { isDivider: true },
                  {
                    label: t('library.rename'),
                    icon: Edit2,
                    shortcut: 'F2',
                    disabled: targetSelection.length > 1,
                    action: () => {
                      openInputDialog({
                        title: t('library.rename'),
                        defaultName: pose.name,
                        onSave: (newName) => renamePose(pose.id, newName)
                      });
                    }
                  },
                  {
                    label: t('library.duplicate'),
                    icon: Copy,
                    shortcut: 'Ctrl+D',
                    action: () => {
                      targetSelection.forEach(id => duplicatePose(id));
                    }
                  },
                  { isDivider: true },
                  {
                    label: t('library.cut'),
                    icon: Scissors,
                    shortcut: 'Ctrl+X',
                    action: handleCutSelected
                  },
                  {
                    label: t('library.copy'),
                    icon: Copy,
                    shortcut: 'Ctrl+C',
                    action: handleCopySelected
                  },
                  {
                    label: t('library.paste'),
                    icon: Clipboard,
                    shortcut: 'Ctrl+V',
                    disabled: !clipboard || clipboard.itemType !== 'pose',
                    action: handlePaste
                  },
                  { isDivider: true },
                  { 
                    label: targetSelection.length > 1 ? t('library.delete_item', { count: targetSelection.length }) : t('library.delete_item'), 
                    icon: Trash2, 
                    className: 'danger',
                    shortcut: 'Del',
                    action: handleDeleteSelected
                  }
                ]);
              }}
            >
              <ThumbnailView pose={pose.pose} cameraState={pose.cameraState} />
              <div className="frame-label pose-name" style={{ fontSize: '0.55rem', fontWeight: 600 }}>
                {pose.name}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hover Preview Pop-up */}
      {hoveredPose && (
        <PosePreviewBox
          pose={hoveredPose.pose}
          cameraState={hoveredPose.cameraState}
          style={{
            position: 'fixed',
            left: `max(100px, min(${hoverPos.x}px, calc(100% - 100px)))`,
            top: hoverPos.y < 350 ? `${hoverPos.y + hoverPos.height + 10}px` : `${hoverPos.y - 10}px`,
            transform: 'none',
            zIndex: 9999,
            pointerEvents: 'auto',
          }}
          onMouseEnter={() => {
            isMouseOverPreviewRef.current = true;
            if (hoverHideTimeoutRef.current) {
              clearTimeout(hoverHideTimeoutRef.current);
              hoverHideTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => {
            isMouseOverPreviewRef.current = false;
            scheduleHidePreview();
          }}
        >
          <PoseNoteField
            label={t('library.pose_note_header', { defaultValue: 'Pose' })}
            value={hoveredPose.pose.notes || ''}
            emptyText={t('timeline.no_pose_note', { defaultValue: 'No pose notes' })}
            onSave={(note) => setSavedPoseNotes(hoveredPose.id, note)}
            onEditingChange={(editing) => { isEditingPoseNoteRef.current = editing; }}
            classNames={{
              cell: 'pose-preview-note-cell',
              headerRow: 'pose-preview-header-row',
              header: 'pose-preview-note-header pose-header',
              iconBtn: 'pose-preview-icon-btn pose-btn',
              body: 'pose-preview-note-body',
              textarea: 'pose-preview-textarea',
            }}
          />
        </PosePreviewBox>
      )}
    </div>
  );

  const titleNode = (
    <div className="explorer-header-title-container">
      <div className="module-icon-box" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '8px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Library size={24} />
      </div>
      <div>
        <h2 style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.02em', color: '#38bdf8' }}>
          {t('library.poses', { defaultValue: 'Poses' })}
        </h2>
      </div>
    </div>
  );

  const breadcrumbPath: { id: string; name: string; parentId: string | null }[] = [];
  if (selectedFolderId !== null) {
    let curr: string | null = selectedFolderId;
    while (curr) {
      const f = folders.find(x => x.id === curr);
      if (!f) break;
      breadcrumbPath.unshift(f);
      curr = f.parentId;
    }
  }

  const contentHeader = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, minHeight: '24px', width: '100%' }}>
      {/* Breadcrumb — left side */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flex: 1, minWidth: 0, overflowX: 'auto', scrollbarWidth: 'none' as const }}>
        <button
          onClick={() => setSelectedFolderId(UNSORTED_POSES_ID)}
          title={t('folderTree.root', { defaultValue: 'Root' })}
          style={{ background: 'transparent', border: 'none', color: selectedFolderId === UNSORTED_POSES_ID ? '#38bdf8' : 'rgba(255,255,255,0.45)', padding: '2px 4px', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
        >
          <Home size={13} />
        </button>
        {breadcrumbPath.map((f, i) => (
          <React.Fragment key={f.id}>
            <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem', flexShrink: 0 }}>/</span>
            <button
              onClick={() => setSelectedFolderId(f.id)}
              className="folder-name"
              style={{ background: 'transparent', border: 'none', opacity: i === breadcrumbPath.length - 1 ? 1 : 0.6, fontSize: '0.75rem', fontWeight: i === breadcrumbPath.length - 1 ? 700 : 500, padding: '2px 4px', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {f.name}
            </button>
          </React.Fragment>
        ))}
      </div>
      {/* Action button — right side */}
      <div style={{ flexShrink: 0, paddingLeft: 16 }}>
        <button
          id="library-new-root-folder-btn"
          onClick={() => handleCreateFolder(null)}
          className="timeline-btn blue"
          style={{ display: 'flex', alignItems: 'center', height: '24px' }}
        >
          <FolderPlus size={14}/>
          <span>{t('folderTree.new_root_folder', { defaultValue: 'New Folder' })}</span>
        </button>
      </div>
    </div>
  );

  const isSidebarExpanded = isMobile && (selectedFolderId === null);
  return <ExplorerView title={titleNode} sidebar={sidebar} content={content} contentHeader={contentHeader} isSidebarExpanded={isSidebarExpanded} />;
};
